<template>
  <section
    v-if="highlights.length > 0"
    class="homepage-highlights"
  >
    <Heading
      tag="h2"
      size="md"
      class="homepage-highlights__heading"
      font-weight="regular"
      spaces-bottom-rem
    >
      Unsere Highlights
    </Heading>

    <DraggableScroller
      ref="draggableScrollbar"
      class="homepage-highlights__list-wrapper"
      has-pagination
      show-single-active-pagination
      pagination-label="Highlights Paginierung"
    >
      <div
        v-for="highlight in highlights"
        :key="highlight.label"
        class="homepage-highlights__list-entry"
      >
        <nuxt-link
          :to="highlight.link"
          class="homepage-highlights__list-item btn btn--no-style"
        >
          <div class="homepage-highlights__visual">
            <CdnImage
              v-if="highlight.brand?.logo"
              class="homepage-highlights__brand"
              render-without-figure
              :src="highlight.brand.logo"
              :image-style="`aspect-ratio: ${highlight.brand.aspectRatio}`"
              :alt="`${highlight.brand.name} Logo`"
              :lazy="true"
            />
            <div
              class="homepage-highlights__item-image-wrapper"
              :class="{
                'homepage-highlights__item-image-wrapper--goliath': highlight.offer?.product,
              }"
              :style="getColorConstants(highlight)"
            >
              <div
                class="homepage-highlights__image-placeholder"
                :class="{
                  'homepage-highlights__image-placeholder--cover':
                    highlight.images?.behaviour === 'cover',
                }"
              >
                <Badge
                  v-if="highlight.badgeMessage"
                  class="homepage-highlights-badge text--bold"
                  style="
                    --badge-xs-size: 1.6em;
                    --badge-xs-padding: 0.4em;
                    --badge-sm-size: 1.7em;
                    --badge-sm-padding: 0.5em;
                  "
                >
                  {{ highlight.badgeMessage }}
                </Badge>
                <!-- No Goliath offer -->
                <template v-if="!highlight.offer?.product && highlight.images">
                  <CdnImage
                    :alt="highlight.label || ''"
                    :src="highlight.images.initial.src"
                    :src-sm="highlight.images.initial.srcSm"
                    class="homepage-highlights__item-image"
                    :class="{
                      'homepage-highlights__item-image--before': highlight.images.hover?.src,
                    }"
                    lazy
                  />
                  <CdnImage
                    v-if="highlight.images.hover?.src"
                    :alt="highlight.label || ''"
                    :src="highlight.images.hover.src"
                    :src-sm="highlight.images.hover.srcSm"
                    class="homepage-highlights__item-image homepage-highlights__item-image--after"
                    lazy
                  />
                </template>
                <template v-else>
                  <GoliathImageElement
                    v-if="highlight.offer?.teaserImage"
                    :product-name="highlight.offer.product?.name || ''"
                    :image="highlight.offer.teaserImage"
                    class="homepage-highlights__item-image homepage-highlights__item-image--before"
                    xs-width="medium"
                    sm-width="large"
                    style="aspect-ratio: 1"
                    lazy
                  />
                  <GoliathImageElement
                    v-if="highlight.goliathHoverImage"
                    :alt="highlight.label || ''"
                    :image="highlight.goliathHoverImage"
                    class="homepage-highlights__item-image homepage-highlights__item-image--after"
                    xs-width="medium"
                    sm-width="large"
                    lazy
                  />
                </template>
              </div>
            </div>
          </div>
          <span class="homepage-highlights__title">
            {{ highlight.label }}
            <span class="homepage-highlights__arrow-symbol">
              <Arrow
                direction="right"
                tag="span"
              />
            </span>
          </span>
        </nuxt-link>
      </div>
    </DraggableScroller>
  </section>
</template>

<script lang="ts" setup>
import { useLazyAsyncData } from '#imports';
import { Arrow, Heading, GoliathImageElement, Badge } from '@i22team/td-longtail-vue-components';
import CdnImage from '~/components/cdn-image.vue';
import DraggableScroller from '~/components/draggable-scroller.vue';
import {
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import { getBrand, type BrandWithLogo } from '~/lib/brand';
import type { LocalImage } from '@i22team/rocket';
import { getPromotionsFromGroup, type DateGroupedPromotions } from '~/lib/promotions';

interface ImageData {
  src: string;
  srcSm: string;
  alt: string;
}

type HighlightPromotions = Promotion<{
  background?: {
    startingColor: string;
    endColor: string;
  };
  name?: string;
  hoverImageIndex?: number;
  label?: string;
  link?: string;
  badgeMessage?: string;
  goliathHoverImage?: LocalImage | undefined;
  brand?: BrandWithLogo | undefined;
  images?: {
    behaviour?: 'cover';
    initial: ImageData;
    hover?: ImageData;
  };
}>;

const manualHighlights: HighlightPromotions[] = [
  // example of how the component expects a manual teaser to be written
  // {
  //   images: {
  //     initial: {
  //       src: '',
  //       srcSm: '',
  //       alt: '',
  //     },
  //     hover: {
  //       src: '',
  //       srcSm: '',
  //       alt: '',
  //     },
  //   },
  //   label: 'Example',
  //   link: '/',
  // },
];

const recommendedProductsGrouped: DateGroupedPromotions<HighlightPromotions>[] = [
  {
    startDate: '01.11.2026',
    endDate: '17.11.2026 23:59',
    promotions: [
      {
        articleNumber: '4250494923484', // Homematic IP Heizkörperthermostat 3er Set
        background: {
          startingColor: '#F8F8F8',
          endColor: '#C9C9C9',
        },
      },
      {
        articleNumber: '0198154668969', // Lenovo Tab 10.1" 4G inkl. Folio Case 64 GB
        background: {
          startingColor: '#E8F0FF',
          endColor: '#D2E0FB',
        },
      },
      {
        articleNumber: '0194644187002', // soundcore Sport X20
        background: {
          startingColor: '#F8F8F8',
          endColor: '#C9C9C9',
        },
      },
      {
        articleNumber: '4260195392649', // Eve Thermostat – Smartes Thermostat für Fußbodenheizungen
        background: {
          startingColor: '#E8F0FF',
          endColor: '#D2E0FB',
        },
      },
    ],
  },
  {
    startDate: '01.10.2026',
    endDate: '31.10.2026 23:59',
    promotions: [
      {
        articleNumber: '6974434224179', // Nothing headphone (1)
        background: {
          startingColor: '#F8F8F8',
          endColor: '#C9C9C9',
        },
      },
      {
        articleNumber: '4048945590954', // Teufel Rockster Neo
        background: {
          startingColor: '#E8F0FF',
          endColor: '#D2E0FB',
        },
      },
      {
        articleNumber: '4029164129545', // Homematic IP Heizkörperthermostat – basic 5er Set
        background: {
          startingColor: '#F8F8F8',
          endColor: '#C9C9C9',
        },
      },
      {
        articleNumber: '4260195392229', // Eve Aqua
        background: {
          startingColor: '#E8F0FF',
          endColor: '#D2E0FB',
        },
      },
    ],
  },
];

const draggableScrollbar = ref<typeof DraggableScroller | null>(null);

const promotions = getPromotionsFromGroup(recommendedProductsGrouped);

const getProductTitle = (offer: ListOffer | undefined) => {
  const shortName = promotions.find(
    (gp) => gp.articleNumber === offer?.product?.articleNumber
  )?.name;
  return shortName || offer?.product?.family?.name;
};

const getOfferLink = (offer: ListOffer | undefined) => `/geraete/${offer?.product?.slug}`;

const getColorConstants = (highlight: any) =>
  `--starting-color: ${highlight.background?.startingColor || '#e7e7e7'}; --end-color: ${
    highlight.background?.endColor || 'white'
  }`;

const { data: goliathHighlights, error: errorData } = useLazyAsyncData(
  'homepage-highlights',
  async (): Promise<PromotionOffer<HighlightPromotions>[]> => {
    try {
      const response = await loadOffersForPromotions<HighlightPromotions>(promotions, false, false);

      setTimeout(() => {
        // Update scroller for new pages
        draggableScrollbar.value?.updateScroller();
      }, 1);

      return response.map((result) => ({
        ...result,
        goliathHoverImage:
          getMainImageFromOffer(result.offer, result.hoverImageIndex || 1) ||
          getMainImageFromOffer(result.offer, 0),
        label: result.label || getProductTitle(result.offer) || undefined,
        link: result.link || getOfferLink(result.offer),
        brand: getBrand(result.offer),
      }));
    } catch (e: any) {
      console.error(e);
      return [];
    }
  }
);

// handle errors
if (errorData.value) {
  console.error(errorData.value);
}

const highlights = computed<PromotionOffer<HighlightPromotions>[]>(() => {
  const returnHighlights: PromotionOffer<HighlightPromotions>[] = [];
  manualHighlights.forEach((h) => {
    returnHighlights.push(h);
  });

  goliathHighlights.value?.forEach((h) => {
    returnHighlights.push(h);
  });

  return returnHighlights;
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.homepage-highlights {
  margin-bottom: 2 * $grid-base;

  &__heading {
    padding-inline: $grid-base;
    margin-bottom: $grid-base;
  }

  &__visual {
    width: 100%;
    min-height: 380px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    position: relative;

    @include mq($mq-md) {
      min-height: 450px;
    }
  }

  &__brand {
    position: absolute;
    height: 20px;
    top: $grid-base;
    left: $grid-base;
  }

  &__image-placeholder {
    flex: 0 0 100%;
    aspect-ratio: 1;
    position: relative;

    &--cover {
      height: 100%;
      width: 100%;

      .homepage-highlights__item-image {
        height: 100%;
      }

      :deep(.image-element) {
        img {
          object-fit: cover;
          height: 100%;
        }
      }
    }
  }

  &__image-logo {
    position: absolute;
    left: $grid-base;
    bottom: $grid-base;
    width: 35%;
  }

  &__item-image {
    width: 100%;

    :deep(img) {
      height: 100%;
      width: 100%;
      object-fit: cover;
      object-position: center;
      margin-top: $grid-base + 20px; // logo
    }

    &-wrapper {
      overflow: hidden;
      transition: all 0.3s ease-out;
      min-height: 100%;
      width: 100%;
      display: flex;
      align-items: center;

      &--goliath {
        background-image: linear-gradient(to bottom, var(--starting-color), var(--end-color));
        padding: $grid-base;
      }
    }

    &--after {
      display: none;
    }
  }

  &__arrow-symbol {
    position: relative;
    display: inline-flex;
    gap: 2px;
    bottom: 5px;
    &::before {
      content: '';
      border-top: 1.5px solid var(--color-font);
      position: relative;
      align-self: center;
      min-width: 0;
      width: auto;

      transition: min-width 0.3s ease-out;
      will-change: min-width;
    }

    .arrow {
      position: relative;
      width: 0.7em;
      height: 0.7em;
    }
  }

  :deep(.draggable-scroller__container) {
    display: flex;
    width: 100%;

    @include mq(false, $mq-md - 1) {
      scroll-snap-type: x mandatory;
    }
  }

  &__list {
    &-entry {
      flex: 0 0 100%;
      padding: 2px;
      margin-bottom: 5px; // small margin to avoid vertical scrolling

      @include mq(false, $mq-md - 1) {
        scroll-snap-align: start;
      }

      @include mq($mq-sm) {
        flex-basis: 50%;
      }

      @include mq($mq-md) {
        flex-basis: 33.333%;
      }
      @include mq($mq-lg) {
        flex-basis: 25%;
      }
    }

    &-wrapper {
      margin: 0 auto;
    }

    &-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: $grid-base;
      white-space: normal;
      text-align: left;
      padding-inline: $grid-base;

      &:hover,
      &:focus {
        color: var(--color-font);
      }

      &:focus-visible {
        outline-offset: 0;
        outline: var(--focus-outline);
      }

      &:hover {
        .homepage-highlights__item-image {
          &-wrapper {
            border-radius: 2 * $grid-base;
          }

          &--before {
            display: none;
          }

          &--after {
            display: block;
          }
        }

        .homepage-highlights__arrow-symbol {
          &::before {
            min-width: $grid-base;
          }
        }
      }
    }
  }
  &-badge {
    z-index: 1;
  }
}
</style>
